import React from 'react';
import { Box, Typography, Stack } from '@mui/material';
import {
    LoginRounded,
    LogoutRounded,
    LocalCafeRounded,
    TimerOffRounded,
    WorkHistoryRounded,
} from '@mui/icons-material';

import { DASH, EVENT_TONES } from '../dashboard/dashboardTokens';

const KIND_ICON = {
    arrival: LoginRounded,
    departure: LogoutRounded,
    auto_end: TimerOffRounded,
    break_start: LocalCafeRounded,
    status: WorkHistoryRounded,
};

const KIND_TONE = {
    arrival: EVENT_TONES.arrival,
    departure: EVENT_TONES.departure,
    auto_end: EVENT_TONES.departure,
    break_start: EVENT_TONES.break_start,
    status: '#7C3AED',
};

const formatMinutes = (minutes) => {
    const total = Math.round(minutes || 0);
    if (total <= 0) return null;
    const h = Math.floor(total / 60);
    const m = total % 60;
    return h > 0 ? `${h} h ${m} m` : `${m} m`;
};

/**
 * Deň ako časová os — rovnaká vizuálna reč ako nástenka na prehľade, aby sa
 * príchod a odchod nečítali na dvoch miestach dvoma spôsobmi.
 */
const DayTimeline = ({ events, t }) => {
    if (!events.length) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                {t('pages.employeeProfile.day.noRecords')}
            </Typography>
        );
    }

    return (
        <Box>
            {events.map((event, idx) => {
                const Icon = KIND_ICON[event.kind] || LoginRounded;
                const tone = KIND_TONE[event.kind] || EVENT_TONES.departure;
                const duration = formatMinutes(event.minutes);
                const label = event.kind === 'status'
                    ? (event.label || t('pages.employeeProfile.day.kinds.status'))
                    : t(`pages.employeeProfile.day.kinds.${event.kind}`);

                return (
                    <Box
                        key={`${event.time}-${event.kind}-${idx}`}
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            py: 1,
                            // Os prechádza celým riadkom, uzol ju prekryje.
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: 63,
                                top: 0,
                                bottom: 0,
                                width: '1px',
                                bgcolor: 'divider',
                            },
                            '&:first-of-type::before': { top: '50%' },
                            '&:last-of-type::before': { bottom: '50%' },
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            sx={{ width: '100%', position: 'relative', zIndex: 1 }}
                        >
                            <Typography
                                sx={{
                                    width: 50,
                                    flexShrink: 0,
                                    fontSize: '0.875rem',
                                    fontWeight: 700,
                                    ...DASH.numeric,
                                }}
                            >
                                {event.time}
                            </Typography>

                            <Box
                                sx={(theme) => ({
                                    width: 28,
                                    height: 28,
                                    flexShrink: 0,
                                    mx: '-1px',
                                    borderRadius: '50%',
                                    bgcolor: tone,
                                    boxShadow: `0 0 0 4px ${theme.palette.background.paper}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    '& svg': { fontSize: 15, color: '#fff' },
                                })}
                            >
                                <Icon />
                            </Box>

                            <Box sx={{ minWidth: 0, flex: 1, pl: 2 }}>
                                <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.3 }} noWrap>
                                    {label}
                                </Typography>
                                {event.label && event.kind !== 'status' && (
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                        {event.label}
                                    </Typography>
                                )}
                            </Box>

                            {duration && (event.kind === 'break_start' || event.kind === 'status') && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ flexShrink: 0, ...DASH.numeric }}
                                >
                                    {duration}
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                );
            })}
        </Box>
    );
};

export default DayTimeline;
